export interface ICaseManagementEndpoints {
  getCourtInfo: (orgId: string) => string;
  getMaintenanceActions: (orgId: string) => string;
  getCourts: () => string;
  caseSearch: (orgId: string) => string;
  caseSearchByCaseClassId: (orgId: string, caseClassId: number) => string;
  partySearch: (term: string) => string;
  getParticipants: (orgId: string) => string;
  getAttorneys: (orgId: string) => string;
  getCase: (orgId: string, caseId: number) => string;
  applyOrQueueNewCase: (orgId: string) => string;
  applyOrQueueCaseUpdate: (orgId: string, caseId: number) => string;
  updateNewQueueCase: (orgId: string, entryId: number) => string;
  getParticipantsByCourt: (orgId: string) => string;
  getParticipantsByCase: (orgId: string, caseId: number) => string;
  getParticipantsByAttorney: (orgId: string, attorneyId: number) => string;
  getParticipantDetail: (orgId: string, caseId: number, partyId: number, partyTypeId: number) => string;
  getCourtPersonnel: (orgId: string, term: string) => string;
  getCourtPersonnelByCase: (orgId: string, caseId: number) => string;
  getCasesByPersonnel: (orgId: string, personId: number) => string;
  getCaseStatusCatalog: (orgId: string) => string;
  getCaseClassCatalog: (orgId: string) => string;
  getCaseTypeCatalog: (orgId: string, caseClassId: number) => string;
  getCaseTypeCatalogByCourtUniqueId: (courtUniqueId: string, caseClassId: number) => string;
  getDivisionCatalog: (orgId: string) => string;
  getJudgeCatalog: (orgId: string) => string;
  searchJudgeCatalog: (orgId: string) => string;
  getClerkCatalog: (orgId: string) => string;
  searchClerkCatalog: (orgId: string) => string;
  getAttorneyTypeCatalog: (orgId: string) => string;
  getCourtCaseClassInfo: (orgId: string, caseClassId: number) => string;
  getCaseManagementCatalogs: (orgId: string, catalogs?: string[]) => string;
  attorneySearch: (orgId: string, term: string, searchInMyFirm: boolean) => string;
  addJudgesClerksToCourtCatalog: (orgId: string) => string;
  getLowerCourts: (orgId: string) => string;
  tempDocuments: string;
  appealableCaseSearch: (orgId: string) => string;
  caseGroupSearch: (orgId: string) => string;
  getCaseGroup: (orgId: string, groupId: number) => string;
  caseSearchByCaseGroup: (orgId: string, groupId: number) => string;
  addCaseGroup: (orgId: string) => string;
  deleteCaseGroup: (orgId: string, groupId: number) => string;
  removeCaseFromCaseGroup: (orgId: string, groupId: number) => string;
  addCasesToCaseGroup: (orgId: string, groupId: number) => string;

  getQueueCaseList: (orgId: string) => string;
  getQueueAttorneyList: (orgId: string) => string;
  getQueueParticipantList: (orgId: string) => string;
  getQueueParticipantEntry: (orgId: string, entryId: number) => string;
  getQueueCourtPersonnelList: (orgId: string) => string;

  approveQueueCaseList: (orgId: string) => string;
  approveQueueAttorneyList: (orgId: string) => string;
  approveQueueParticipantList: (orgId: string) => string;
  approveQueueCourtPersonnelList: (orgId: string) => string;

  getQueueCaseEntry: (orgId: string, entryId: number) => string;
  getQueueCourtPersonnelEntry: (orgId: string, entryId: number) => string;
  getQueueAttorneyEntry: (orgId: string, entryId: number) => string;

  getCaseLoadList: (orgId: string, uploadType: string) => string;
}

export const caseManagementEndpoints = (versionedUrl: string): ICaseManagementEndpoints => {
  const courtControllerURL = `${versionedUrl}/courts`;
  const courtController_CourtIdURL = (orgId: string) =>`${versionedUrl}/courts/${orgId}`;
  const caseControllerURL = (orgId: string) => `${versionedUrl}/courts/${orgId}/cases`;
  const documentsControllerURL = `${versionedUrl}/documents`;

  return {
    getCourtInfo: (orgId: string) => `${courtControllerURL}/${orgId}`,
    getMaintenanceActions: (orgId: string) => `${courtControllerURL}/${orgId}/maintenance-actions`,
    getCourts: () => `${courtControllerURL}/allow-maintenance`,
    partySearch: (term: string) => `${versionedUrl}/parties?term=${term}`,
    getParticipants: (orgId: string) => `${courtControllerURL}/${orgId}/participants`,
    getAttorneys: (orgId: string) => `${courtControllerURL}/${orgId}/attorneys`,
    caseSearch: (orgId: string) => caseControllerURL(orgId),
    caseSearchByCaseClassId: (orgId: string, caseClassId: number) => `${caseControllerURL(orgId)}/caseclass/${caseClassId}`,
    getCase: (orgId: string, caseId: number) => `${caseControllerURL(orgId)}/${caseId}`,
    updateNewQueueCase: (orgId: string, entryId: number) => `${caseControllerURL(orgId)}/queues/${entryId}`,
    applyOrQueueCaseUpdate: (orgId: string, caseId: number) => `${caseControllerURL(orgId)}/${caseId}`,
    applyOrQueueNewCase: (orgId: string) => caseControllerURL(orgId),
    getParticipantsByCourt: (orgId: string) => `${courtController_CourtIdURL(orgId)}/participants`,
    getParticipantsByCase: (orgId: string, caseId: number) => `${caseControllerURL(orgId)}/${caseId}/participants`,
    getParticipantsByAttorney: (orgId: string, attorneyId: number) => `${courtController_CourtIdURL(orgId)}/attorneys/${attorneyId}/participants`,
    getCourtPersonnel: (orgId: string, term: string) => `${courtControllerURL}/${orgId}/personnel?term=${term}`,
    getCourtPersonnelByCase: (orgId: string, caseId: number) => `${caseControllerURL(orgId)}/${caseId}/personnel`,
    getCasesByPersonnel: (orgId: string, personId: number) => `${courtController_CourtIdURL(orgId)}/personnel/${personId}/cases`,
    getParticipantDetail: (orgId: string, caseId: number, partyId: number, partyTypeId: number) => `${caseControllerURL(orgId)}/${caseId}/participants/${partyId}/type/${partyTypeId}`,
    getCaseStatusCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/casestatuses`,
    getCaseClassCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/caseclasses`,
    getCaseTypeCatalog: (orgId: string, caseClassId: number) => `${courtController_CourtIdURL(orgId)}/catalog/casetypes/${caseClassId}`,
    getCaseTypeCatalogByCourtUniqueId: (courtUniqueId: string, caseClassId: number) => `${versionedUrl}/courts/${courtUniqueId}/catalog/casetypes/${caseClassId}`,
    getDivisionCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/divisions`,
    getJudgeCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/judges`,
    searchJudgeCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/judges/search`,
    getClerkCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/clerks`,
    searchClerkCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/clerks/search`,
    getAttorneyTypeCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/attorneytypes`,
    getCourtCaseClassInfo: (orgId: string, caseClassId: number) => `${courtController_CourtIdURL(orgId)}/caseclasses/${caseClassId}`,
    getCaseManagementCatalogs: (orgId: string, catalogs: string[] = []) => `${courtController_CourtIdURL(orgId)}/catalog/case-management?q=${catalogs.join()}`,
    attorneySearch: (orgId: string, term: string, searchInMyFirm: boolean) => `${courtController_CourtIdURL(orgId)}/attorneys?term=${term}${searchInMyFirm ? '&searchInMyFirm=true': ''}`,
    addJudgesClerksToCourtCatalog: (orgId: string) => `${courtController_CourtIdURL(orgId)}/catalog/judges-clerks`,
    getLowerCourts: (orgId: string) => `${courtController_CourtIdURL(orgId)}/lower-courts`,
    tempDocuments: `${documentsControllerURL}/temp`,
    appealableCaseSearch: (orgId: string) => `${caseControllerURL(orgId)}/appealable`,
    caseGroupSearch: (orgId: string) => `${courtController_CourtIdURL(orgId)}/case-groups`,
    getCaseGroup: (orgId: string, groupId: number) => `${courtController_CourtIdURL(orgId)}/case-groups/${groupId}`,
    caseSearchByCaseGroup: (orgId: string, groupId: number) => `${courtController_CourtIdURL(orgId)}/case-groups/${groupId}/cases`,
    addCaseGroup: (orgId: string) => `${courtController_CourtIdURL(orgId)}/case-groups`,
    deleteCaseGroup: (orgId: string, groupId: number) => `${courtController_CourtIdURL(orgId)}/case-groups/${groupId}`,
    removeCaseFromCaseGroup: (orgId: string, groupId: number) => `${courtController_CourtIdURL(orgId)}/case-groups/${groupId}/cases`,
    addCasesToCaseGroup: (orgId: string, groupId: number) => `${courtController_CourtIdURL(orgId)}/case-groups/${groupId}/cases`,

    getQueueCaseList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/cases/queues`,
    getQueueAttorneyList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/attorneys/queues`,
    getQueueParticipantList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/participants/queues`,
    getQueueParticipantEntry: (orgId: string, entryId: number) => `${courtController_CourtIdURL(orgId)}/participants/queues/${entryId}`,
    getQueueCourtPersonnelList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/personnel/queues`,

    approveQueueCaseList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/cases/queues/approve`,
    approveQueueAttorneyList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/attorneys/queues/approve`,
    approveQueueParticipantList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/participants/queues/approve`,
    approveQueueCourtPersonnelList: (orgId: string) => `${courtController_CourtIdURL(orgId)}/personnel/queues/approve`,

    getQueueCaseEntry: (orgId: string, entryId: number) => `${courtController_CourtIdURL(orgId)}/cases/queues/${entryId}`,
    getQueueCourtPersonnelEntry: (orgId: string, entryId: number) => `${courtController_CourtIdURL(orgId)}/personnel/queues/${entryId}`,
    getQueueAttorneyEntry: (orgId: string, entryId: number) => `${courtController_CourtIdURL(orgId)}/attorneys/queues/${entryId}`,
    
    getCaseLoadList: (orgId: string,uploadType: string) => `${courtController_CourtIdURL(orgId)}/case-load`,
  }
}
